import { CookieService } from "@biskoui/cookie-banner";

import { CDN_URL, API_URL } from "./config";
import { IS_BOT } from "./botDetection";
import { LanguageCode, translations } from "./texts";
import { CookieBanner } from "./types";
import { setItem, getItem, removeItem } from "./storage";
import { randomUUID, SESSION_ID } from "./utils";

//@ts-ignore
function dataLayerPush() {
  //@ts-ignore
  window.dataLayer.push(arguments);
}


// The visitor ID is stored in order to link the visitor to the backend history of accepted permissions.
// They are needed also to revoke permissions. Note that we don't generate and keep a visitor ID for visitor
// that simply reject all cookie, we only do so if they accept at least one.
// Visitors that reject all services will still have the local storage above stored for accepted services. This
// is so that we don't ask them each time and instead automatically process the choice. That said we never send
// that information so it can really be considered as a local preference without any way to track or identify the user.
const BISKOUI_STORAGE_VISITOR_ID = "biskoui__visitorId";

const BISKOUI_STORAGE_ACCEPTED_SERVICES = "biskoui__acceptedServices";

// Function called for every service when initializing the banner. This processes the entire DOM.
// This is called before triggering
// any accept action for services that are already accepted from a previous session.
export function initializeServiceGlobally(
  cookieService: CookieService,
  cookieBanner: CookieBanner,
  onAcceptService: () => void
) {
  // TODO: For now we just do the built-in action from automated service. But in
  // the future we should also trigger scripts hosted by biskoui to be ran at load time (new feature).
  if (cookieService.integrationMode == "automated") {
    if (cookieService.stableName == "youtube") {
      injectYoutubePlaceholders(cookieBanner.language, onAcceptService);
    } else if (cookieService.stableName == "vimeo") {
      injectVimeoPlaceholders(cookieBanner.language, onAcceptService);
    } else if (cookieService.stableName == "google_maps") {
      injectGoogleMapsPlaceholders(cookieBanner.language, onAcceptService);
    } else if (cookieService.stableName == "open_street_map") {
      injectOpenStreetMapPlaceholders(cookieBanner.language, onAcceptService);
    }
  }
}

// This is called on new node added to the DOM dynamically. This is not used during the initial loading of the
// page, but instead this is used on every new node added and can handle things like lightboxes.
export function initializeServiceDynamically(
  cookieService: CookieService,
  cookieBanner: CookieBanner,
  node: HTMLElement,
  onAcceptService: () => void
) {
  if (cookieService.integrationMode == "automated") {
    if (cookieService.stableName == "youtube") {
      let el = node.getElementsByTagName("iframe");
      for (var i = 0; i < el.length; i++) {
        injectYoutubePlaceholder(
          cookieBanner.language,
          el[i],
          onAcceptService,
          true
        );
      }

      // We need to immediately trigger the action since this was shown dynamically
      if (
        readAcceptedServices(cookieBanner)?.find(
          (cs) => cs.stableName === "youtube"
        )
      ) {
        enableYoutubeVideos();
      }
    } else if (cookieService.stableName == "vimeo") {
      let el = node.getElementsByTagName("iframe");
      for (var i = 0; i < el.length; i++) {
        injectVimeoPlaceholder(
          cookieBanner.language,
          el[i],
          onAcceptService,
          true
        );
      }

      // We need to immediately trigger the action since this was shown dynamically
      if (
        readAcceptedServices(cookieBanner)?.find(
          (cs) => cs.stableName === "vimeo"
        )
      ) {
        enableVimeoVideos();
      }
    } else if (cookieService.stableName == "google_maps") {
      // TODO
    } else if (cookieService.stableName == "open_street_map") {
      // TODO
    }
  }
}

// Execute accept all action. This must be called when the visitor explicitly
// accepts all the cookies, and not just to trigger the effect of accepting the
// cookies (on a reload).
export function acceptAll(cookieBanner: CookieBanner) {
  logConsentment(
    cookieBanner,
    "accept-all: " +
      cookieBanner.cookieServices.map((s) => s.service).join(", ")
  );

  // TODO: handle the exact list of services in case the banner changes its list of services.
  setItem(
    BISKOUI_STORAGE_ACCEPTED_SERVICES,
    JSON.stringify("accept-all"),
    cookieBanner
  );
  for (var i = 0; i < cookieBanner.cookieServices.length; i++) {
    let cookieService = cookieBanner.cookieServices[i];
    updateConsent(cookieService, cookieBanner, "granted");
    activateService(cookieService);
  }
}

export function rejectAll(cookieBanner: CookieBanner) {
  logConsentment(cookieBanner, "reject-all");
  setItem(
    BISKOUI_STORAGE_ACCEPTED_SERVICES,
    JSON.stringify("reject-all"),
    cookieBanner
  );

  for (var i = 0; i < cookieBanner.cookieServices.length; i++) {
    updateConsent(cookieBanner.cookieServices[i], cookieBanner, "denied");
  }
}

// Execute accept customized action. This is called when the visitor has accepted a set of
// individual services instead of a blanket accept-all.
export function acceptCustomized(
  cookieBanner: CookieBanner,
  services: CookieService[]
) {
  logConsentment(
    cookieBanner,
    "customized: " + services.map((s) => s.service).join(", ")
  );
  setItem(
    BISKOUI_STORAGE_ACCEPTED_SERVICES,
    JSON.stringify(services.map((s) => s.id)),
    cookieBanner
  );

  for (var i = 0; i < cookieBanner.cookieServices.length; i++) {
    let cookieService = cookieBanner.cookieServices[i];
    if (services.some((s) => s.id == cookieService.id)) {
      updateConsent(cookieService, cookieBanner, "granted");
      activateService(cookieService);
    } else {
      updateConsent(cookieService, cookieBanner, "denied");
    }
  }
}

var consentState = {
  analytics: "",
  ads: "",
};
var uetConsentState = {
  ads: "",
}

export function updateConsent(
  cookieService: CookieService,
  cookieBanner: CookieBanner,
  newState: string
) {

  if (
    cookieBanner.googleConsentMode !== "disabled" &&
    cookieService.stableName == "google_analytics" &&
    consentState.analytics != newState
  ) {
    //@ts-ignore
    dataLayerPush("consent", "update", {
      analytics_storage: newState,
    });
    consentState.analytics = newState;
  } else if (
    cookieBanner.googleConsentMode !== "disabled" &&
    (cookieService.stableName == "google_ads" ||
      cookieService.stableName == "google_conversion_linker" ||
      cookieService.stableName == "google_ads_remarketing") &&
    consentState.ads != newState
  ) {
    //@ts-ignore
    dataLayerPush("consent", "update", {
      ad_storage: newState,
      ad_user_data: newState,
      ad_personalization: newState,
      personalization_storage: newState,
    });
    consentState.ads = newState;
  } else if ( 
    cookieBanner.uetConsentMode !== "disabled" &&
    cookieService.stableName == "ms_advertising" &&
    uetConsentState.ads != newState
  ) {
    //@ts-ignore
    window.uetq.push("consent", "update", {
      ad_storage: newState,
    });
    uetConsentState.ads = newState;
  }
}

// We store a cache of executed services during the page as we don't want to execute the same script twice.
// Currently we don't support any way to undo the execution of a script, so if the visitor accept and then
// reject the same script, we won't undo anything on the rejection (but we won't load it on the next page load).
// However if the visitor accepts yet again after rejecting, on the same session, then we must not execute again,
// hence this cache.
var executedServices: { [key: number]: boolean } = {};

// This activates a service by executing the action corresponding to the integration mode of the
// service:
//   - Run the code stored in biskoui
//   - Run the service action managed by biskoui
//   - Trigger the GTM event to activate the service
// Only fire once. This does nothing if called more than once.
export function activateService(cookieService: CookieService) {
  if (
    cookieService.id in executedServices &&
    executedServices[cookieService.id]
  ) {
    return;
  }
  executedServices[cookieService.id] = true;

  // We always notify a global window event that the service is activated (e.g. biskoui:google_analytics_activated).
  // TODO: we could probably use this as the only action to make here, and define the rest
  // as event listener to these events?
  // TODO: The semantics of this is to be triggered only once per page load, even if the user activate, then de-activate, then
  // activate again. This is not a big deal in my opinion, because 99.99% of the use cases are happy with this behavior, but
  // worth noting.
  // We wrap in a setTimeout to make it run async and only notify the clients after everything has been done in biskoui.
  setTimeout(() => window.dispatchEvent(
    new Event(`biskoui:${cookieService.stableName}_activated`)
  ));

  if (cookieService.integrationMode == "biskoui") {
    var d = new DOMParser().parseFromString(cookieService.script, "text/html");
    var scripts = d.getElementsByTagName("script");
    for (var s of scripts) {
      // We need to create a brand new script object, and cloning the properties of interest.
      var cloneScript = document.createElement("script");
      // Very important to not set the empty src attribute, otherwise when appending the script it won't execute the innerHTML.
      if (s.src) {
        cloneScript.src = s.src;
      }
      cloneScript.async = s.async;
      cloneScript.innerHTML = s.innerHTML;
      // This should execute the script as originally expected.
      document.body.appendChild(cloneScript);
    }
  } else if (cookieService.integrationMode == "gtm") {
    // We trigger an event for GTM to let it know that it should active the service.
    // Note that we only trigger the event once and when the service is accepted. We don't trigger anything when
    // the service is rejected. Maybe we could in addition trigger accept/reject events?

    if (cookieService.activateEventName) {
      //@ts-ignore
      window.dataLayer.push({ event: cookieService.activateEventName });
    }
  } else if (cookieService.integrationMode == "automated") {
    invokeManagedAction(cookieService.stableName);
  }
}

const logConsentment = (cookieBanner: CookieBanner, consentment: string) => {
  var visitorId = getItem(BISKOUI_STORAGE_VISITOR_ID, cookieBanner);
  if (!visitorId && consentment != "reject-all") {
    // If we have no visitor id and we are not doing a reject all, we should create a visitor ID.
    // This will serve to track the visitor over time if they update their preferences. If the initial
    // preferences is to reject-all, we do not actually want to track the user because there's nothing
    // to update, so we not generate an ID and not store anything on the server. If that visitor starts
    // accepting cookie in a future session, they will get an id at this time and there won't be any way
    // to link them to this initial reject all.
    // Note that if the visitor ID was already set and there's a reject-all, we will not enter this condition.
    // We must handle below the revoke of permissions for all cookies, so we need the ID that was generated before,
    // and after revoking we will erase the ID.
    visitorId = randomUUID();
    setItem(BISKOUI_STORAGE_VISITOR_ID, visitorId, cookieBanner);
  }

  // If it's a bot, we don't send the consent to the server.
  if (IS_BOT) return;

  // We always log this to the server, the server will then decide what to do with the consent.
  // The server will not store it in any queryable format if the user is not at least PLUS, but
  // it will still collect it in aggregate in order to compute statistics.
  fetch(`${API_URL}/v1/cookie-banners/${cookieBanner.id}/consent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      sessionId: SESSION_ID,
      visitorId: visitorId,
      consentment: consentment,
    }),
  }).then((res) => {
    if (res.status == 204) {
      // successfully logged consent.
      if (consentment == "reject-all" || consentment == "customized: ") {
        // We no longer track this user because they have no existing consents.
        // The POST request may have sent a visitor ID if this visitor had previously accepted some cookies and
        // thus had this ID set. This is necessary to revoke on the server. If this ID was not set (first visit)
        // then it would not have been set anyway so there's nothing to remove. The POST is done in any case for
        // statistics, but it's fully anonymous and nothing is logged and stored on the server.
        removeItem(BISKOUI_STORAGE_VISITOR_ID, cookieBanner);
      }
    }
  });
};

// Read currently stored accepted services, returns undefined if nothing was saved yet.
export function readAcceptedServices(
  cookieBanner: CookieBanner
): CookieService[] | undefined {
  var acceptedServices = getItem(
    BISKOUI_STORAGE_ACCEPTED_SERVICES,
    cookieBanner
  );
  if (!acceptedServices) return undefined;

  // Since we already saved the user choice, we will hide the banner (which means show the sticker) and
  // apply their choice immediately.
  var parsedAcceptedServices = JSON.parse(acceptedServices);
  if (parsedAcceptedServices == "accept-all") {
    return cookieBanner.cookieServices;
  } else if (parsedAcceptedServices == "reject-all") {
    return [];
  } else {
    let services: CookieService[] = [];
    parsedAcceptedServices.forEach((obj: any) => {
      if (typeof obj == "object") {
        // This is the legacy situation where we have stored the entire cookie service object.
        let cs = cookieBanner.cookieServices.find((cs) => cs.id === obj.id);
        if (cs) services.push(cs);
      } else {
        // With the new version of the code, we store the list of id only.
        let cs = cookieBanner.cookieServices.find((cs) => cs.id === obj);
        if (cs) services.push(cs);
      }
    });
    return services;
  }
}

/*
 * This is a collection of managed action for each service when the service is accepted.
 * This is invoked when the banner is using the "managed" integration mode, which means they let
 * Biskoui do the action and only provide a few parameters that are service-specific (for example with GA it would be the
 * GA identifier only) and in some cases no parameter are needed (for example in the case of youtube autoblock).
 */

function isYoutubeUrl(url: string) {
  return (
    url.startsWith("https://www.youtube.com") ||
    url.startsWith("https://www.youtube-nocookie.com")
  );
}

var youtubeFrames: any = [];

function enableYoutubeVideos() {
  for (var i = 0; i < youtubeFrames.length; i++) {
    let placeholder = youtubeFrames[i][0];
    let iframe = youtubeFrames[i][1];
    placeholder.remove();
    iframe.style.display = "inline-block";
    let dataSrc = iframe.getAttribute("data-src");
    iframe.setAttribute("src", dataSrc);
    iframe.removeAttribute("data-src");
  }
  youtubeFrames = [];
}

export function injectYoutubePlaceholder(
  language: LanguageCode,
  node: HTMLElement,
  acceptYoutube: () => void,
  isDynamic: boolean // If this is injected on a node that was dynamically added to the DOM.
) {
  const t = translations(language);

  let src = node.getAttribute("src") || "";
  let dataSrc = node.getAttribute("data-src") || "";
  if (isDynamic) {
    if (!isYoutubeUrl(src) && !isYoutubeUrl(dataSrc)) return;
    if (src) {
      node.setAttribute("data-src", src);
      node.removeAttribute("src");
    }
  } else {
    // If this is not dynamically loaded (if it was initialized on first load), we don't want to support 'src'
    // attribute, because there will be a brief time where the src is active and the website will send data
    // to the third-party service. So we don't want to mislead the user by making it look like the blocker is working
    // even though it's not 100% compliant.
    if (!isYoutubeUrl(dataSrc)) return;
  }

  let placeholder = document.createElement("div");

  // Let's keep some common style from the original.
  placeholder.style.position = node.style.position;
  placeholder.style.left = node.style.left;
  placeholder.style.top = node.style.top;
  placeholder.style.right = node.style.right;
  placeholder.style.bottom = node.style.bottom;
  placeholder.style.width = node.style.width;
  placeholder.style.height = node.style.width;
  placeholder.style.display = node.style.width;

  // Now we override the most common width/height set on the iframe itself.
  let width = node.getAttribute("width");
  let height = node.getAttribute("height");
  if (width) placeholder.style.width = width + "px";
  if (height) placeholder.style.height = height + "px";

  placeholder.classList.add("biskoui-youtube-placeholder");

  let icon = document.createElement("img");
  icon.src = CDN_URL + "/youtube_blocked.svg";
  placeholder.append(icon);

  let leadText = document.createElement("div");
  leadText.innerText = t.placeholders.youtube.leadText;
  leadText.style.fontWeight = "700";
  leadText.style.margin = "15px";
  placeholder.append(leadText);

  let text = document.createElement("div");
  text.innerText = t.placeholders.youtube.text;
  text.style.marginBottom = "30px";
  text.style.width = "70%";
  placeholder.append(text);

  let button = document.createElement("button");
  button.innerText = t.placeholders.youtube.enable;
  button.onclick = acceptYoutube;
  placeholder.append(button);

  node.style.display = "none";
  node.after(placeholder);
  youtubeFrames.push([placeholder, node]);
}

function injectYoutubePlaceholders(
  language: LanguageCode,
  acceptYoutube: () => void
) {
  let el = document.getElementsByTagName("iframe");
  for (var i = 0; i < el.length; i++) {
    injectYoutubePlaceholder(language, el[i], acceptYoutube, false);
  }
}

function isVimeoUrl(url: string) {
  return url.startsWith("https://player.vimeo.com");
}

var vimeoFrames: any = [];

function enableVimeoVideos() {
  for (var i = 0; i < vimeoFrames.length; i++) {
    let placeholder = vimeoFrames[i][0];
    let iframe = vimeoFrames[i][1];
    placeholder.remove();
    iframe.style.display = "inline-block";
    let dataSrc = iframe.getAttribute("data-src");
    iframe.setAttribute("src", dataSrc);
    iframe.removeAttribute("data-src");
  }
  vimeoFrames = [];
}

function injectVimeoPlaceholder(
  language: LanguageCode,
  node: HTMLElement,
  acceptVimeo: () => void,
  isDynamic: boolean
) {
  const t = translations(language);

  let src = node.getAttribute("src") || "";
  let dataSrc = node.getAttribute("data-src") || "";
  if (isDynamic) {
    if (!isVimeoUrl(src) && !isVimeoUrl(dataSrc)) return;
    if (src) {
      node.setAttribute("data-src", src);
      node.removeAttribute("src");
    }
  } else {
    // If this is not dynamically loaded (if it was initialized on first load), we don't want to support 'src'
    // attribute, because there will be a brief time where the src is active and the website will send data
    // to the third-party service. So we don't want to mislead the user by making it look like the blocker is working
    // even though it's not 100% compliant.
    if (!isVimeoUrl(dataSrc)) return;
  }

  let placeholder = document.createElement("div");
  let computedStyle = window.getComputedStyle(node);

  placeholder.style.width = node.style.width;
  placeholder.style.height = node.style.height;
  // Now we override the most common width/height set on the iframe itself.
  let width = node.getAttribute("width");
  let height = node.getAttribute("height");
  if (width) placeholder.style.width = width + "px";
  if (height) placeholder.style.height = height + "px";
  // Finally, if there's a computed style, we impose it.
  if (computedStyle.width) placeholder.style.width = computedStyle.width;
  if (computedStyle.height) placeholder.style.height = computedStyle.height;

  // Let's keep some common style from the original.
  placeholder.style.position = node.style.position;
  placeholder.style.left = node.style.left;
  placeholder.style.top = node.style.top;
  placeholder.style.right = node.style.right;
  placeholder.style.bottom = node.style.bottom;
  placeholder.style.display = node.style.width;

  placeholder.classList.add("biskoui-vimeo-placeholder");

  let icon = document.createElement("img");
  icon.src = CDN_URL + "/vimeo_blocked.svg";
  placeholder.append(icon);

  let leadText = document.createElement("div");
  leadText.innerText = t.placeholders.vimeo.leadText;
  leadText.style.fontWeight = "700";
  leadText.style.margin = "15px";
  placeholder.append(leadText);

  let text = document.createElement("div");
  text.innerText = t.placeholders.vimeo.text;
  text.style.marginBottom = "30px";
  text.style.width = "70%";
  placeholder.append(text);

  let button = document.createElement("button");
  button.innerText = t.placeholders.vimeo.enable;
  button.onclick = acceptVimeo;
  placeholder.append(button);

  node.style.display = "none";
  node.after(placeholder);
  vimeoFrames.push([placeholder, node]);
}
function injectVimeoPlaceholders(
  language: LanguageCode,
  acceptVimeo: () => void
) {
  let el = document.getElementsByTagName("iframe");
  for (var i = 0; i < el.length; i++) {
    injectVimeoPlaceholder(language, el[i], acceptVimeo, false);
  }
}

function isGoogleMapsUrl(url: string) {
  return url.startsWith("https://www.google.com/maps");
}

var googleMapsFrames: any = [];

function enableGoogleMaps() {
  for (var i = 0; i < googleMapsFrames.length; i++) {
    let placeholder = googleMapsFrames[i][0];
    let iframe = googleMapsFrames[i][1];
    placeholder.remove();
    iframe.style.display = "inline-block";
    let dataSrc = iframe.getAttribute("data-src");
    iframe.setAttribute("src", dataSrc);
    iframe.removeAttribute("data-src");
  }
  googleMapsFrames = [];
}

function injectGoogleMapsPlaceholder(
  language: LanguageCode,
  node: HTMLElement,
  acceptGoogleMaps: () => void,
  isDynamic: boolean
) {
  const t = translations(language);
  let src = node.getAttribute("src") || "";
  let dataSrc = node.getAttribute("data-src") || "";
  if (isDynamic) {
    if (!isGoogleMapsUrl(src) && !isGoogleMapsUrl(dataSrc)) return;
    if (src) {
      node.setAttribute("data-src", src);
      node.removeAttribute("src");
    }
  } else {
    if (!isGoogleMapsUrl(dataSrc)) return;
  }

  let placeholder = document.createElement("div");
  let computedStyle = window.getComputedStyle(node);

  placeholder.style.width = node.style.width;
  placeholder.style.height = node.style.height;
  // Now we override the most common width/height set on the iframe itself.
  let width = node.getAttribute("width");
  let height = node.getAttribute("height");
  if (width) placeholder.style.width = width + "px";
  if (height) placeholder.style.height = height + "px";
  // Finally, if there's a computed style, we impose it.
  if (computedStyle.width) placeholder.style.width = computedStyle.width;
  if (computedStyle.height) placeholder.style.height = computedStyle.height;

  // Let's keep some common style from the original.
  placeholder.style.position = node.style.position;
  placeholder.style.left = node.style.left;
  placeholder.style.top = node.style.top;
  placeholder.style.right = node.style.right;
  placeholder.style.bottom = node.style.bottom;
  placeholder.style.display = node.style.width;

  placeholder.classList.add("biskoui-google-maps-placeholder");

  let icon = document.createElement("img");
  icon.src = CDN_URL + "/google_maps_blocked.svg";
  placeholder.append(icon);

  let leadText = document.createElement("div");
  leadText.innerText = t.placeholders.googleMaps.leadText;
  leadText.style.fontWeight = "700";
  leadText.style.margin = "15px";
  placeholder.append(leadText);

  let text = document.createElement("div");
  text.innerText = t.placeholders.googleMaps.text;
  text.style.marginBottom = "30px";
  text.style.width = "70%";
  placeholder.append(text);

  let button = document.createElement("button");
  button.innerText = t.placeholders.googleMaps.enable;
  button.onclick = acceptGoogleMaps;
  placeholder.append(button);

  node.style.display = "none";
  node.after(placeholder);
  googleMapsFrames.push([placeholder, node]);
}

function injectGoogleMapsPlaceholders(
  language: LanguageCode,
  acceptGoogleMaps: () => void
) {
  let el = document.getElementsByTagName("iframe");
  for (var i = 0; i < el.length; i++) {
    injectGoogleMapsPlaceholder(language, el[i], acceptGoogleMaps, false);
  }
}

function isOpenStreetMapUrl(url: string) {
  return url.startsWith("https://www.openstreetmap.org/export");
}

var openStreetMapFrames: any = [];

function enableOpenStreetMap() {
  for (var i = 0; i < openStreetMapFrames.length; i++) {
    let placeholder = openStreetMapFrames[i][0];
    let iframe = openStreetMapFrames[i][1];
    placeholder.remove();
    iframe.style.display = "inline-block";
    let dataSrc = iframe.getAttribute("data-src");
    iframe.setAttribute("src", dataSrc);
    iframe.removeAttribute("data-src");
  }
  openStreetMapFrames = [];
}

function injectOpenStreetMapPlaceholders(
  language: LanguageCode,
  acceptOpenStreetMap: () => void
) {
  const t = translations(language);
  let el = document.getElementsByTagName("iframe");
  for (var i = 0; i < el.length; i++) {
    let dataSrc = el[i].getAttribute("data-src");
    if (!dataSrc) continue;
    if (isOpenStreetMapUrl(dataSrc)) {
      let placeholder = document.createElement("div");

      // Let's keep some common style from the original.
      placeholder.style.position = el[i].style.position;
      placeholder.style.left = el[i].style.left;
      placeholder.style.top = el[i].style.top;
      placeholder.style.right = el[i].style.right;
      placeholder.style.bottom = el[i].style.bottom;
      placeholder.style.width = el[i].style.width;
      placeholder.style.height = el[i].style.width;
      placeholder.style.display = el[i].style.width;

      // Now we override the most common width/height set on the iframe itself.
      if (el[i].width) placeholder.style.width = el[i].width + "px";
      if (el[i].height) placeholder.style.height = el[i].height + "px";

      placeholder.classList.add("biskoui-open-street-map-placeholder");

      let icon = document.createElement("img");
      icon.src = CDN_URL + "/google_maps_blocked.svg";
      placeholder.append(icon);

      let leadText = document.createElement("div");
      leadText.innerText = t.placeholders.openStreetMap.leadText;
      leadText.style.fontWeight = "700";
      leadText.style.margin = "15px";
      placeholder.append(leadText);

      let text = document.createElement("div");
      text.innerText = t.placeholders.openStreetMap.text;
      text.style.marginBottom = "30px";
      text.style.width = "70%";
      placeholder.append(text);

      let button = document.createElement("button");
      button.innerText = t.placeholders.openStreetMap.enable;
      button.onclick = acceptOpenStreetMap;
      placeholder.append(button);

      el[i].style.display = "none";
      el[i].after(placeholder);
      openStreetMapFrames.push([placeholder, el[i]]);
    }
  }
}
function invokeManagedAction(service: string) {
  if (service == "youtube") {
    enableYoutubeVideos();
  } else if (service == "vimeo") {
    enableVimeoVideos();
  } else if (service == "google_maps") {
    enableGoogleMaps();
  } else if (service == "open_street_map") {
    enableOpenStreetMap();
  }
}
